import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  TextField,
  Button,
  Avatar,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  useTheme,
} from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { toggleTheme } from '../../redux/slices/themeSlice';
import PageHeader from '../../components/common/PageHeader';
import { isValidEmail } from '../../utils/validation';
import { apiService } from '../../services/api';

const Settings: React.FC = () => {
  const theme = useTheme();
  const { user } = useAppSelector((state) => state.auth);
  
  const [profileForm, setProfileForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
  });
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  const { mode } = useAppSelector((state) => state.theme);
  const dispatch = useAppDispatch();
  
  const [preferences, setPreferences] = useState({
    darkMode: mode === 'dark',
    emailNotifications: true,
    desktopNotifications: false,
  });
  
  // Update preferences when theme mode changes
  useEffect(() => {
    setPreferences(prev => ({
      ...prev,
      darkMode: mode === 'dark'
    }));
  }, [mode]);
  
  const [profileErrors, setProfileErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  
  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const [passwordAlert, setPasswordAlert] = useState({
    show: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  
  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileForm({
      ...profileForm,
      [name]: value,
    });
    
    // Clear error when user types
    if (profileErrors[name as keyof typeof profileErrors]) {
      setProfileErrors({
        ...profileErrors,
        [name]: '',
      });
    }
  };
  
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm({
      ...passwordForm,
      [name]: value,
    });
    
    // Clear error when user types
    if (passwordErrors[name as keyof typeof passwordErrors]) {
      setPasswordErrors({
        ...passwordErrors,
        [name]: '',
      });
    }

    // Clear password alert when user starts typing
    if (passwordAlert.show) {
      setPasswordAlert({
        show: false,
        message: '',
        severity: 'success',
      });
    }
  };
  
  const handlePreferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setPreferences({
      ...preferences,
      [name]: checked,
    });
    
  // Toggle theme when dark mode is toggled
  if (name === 'darkMode') {
    dispatch(toggleTheme());
    // Show a success message
    setSnackbar({
      open: true,
      message: `Dark mode ${checked ? 'enabled' : 'disabled'}`,
      severity: 'success',
    });
  }
  };
  
  const validateProfileForm = (): boolean => {
    const errors = {
      firstName: '',
      lastName: '',
      email: '',
    };
    
    let isValid = true;
    
    if (!profileForm.firstName) {
      errors.firstName = 'First name is required';
      isValid = false;
    }
    
    if (!profileForm.lastName) {
      errors.lastName = 'Last name is required';
      isValid = false;
    }
    
    if (!profileForm.email) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!isValidEmail(profileForm.email)) {
      errors.email = 'Invalid email format';
      isValid = false;
    }
    
    setProfileErrors(errors);
    return isValid;
  };
  
  const validatePasswordForm = (): boolean => {
    const errors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    };
    
    let isValid = true;
    
    if (!passwordForm.currentPassword) {
      errors.currentPassword = 'Current password is required';
      isValid = false;
    }
    
    if (!passwordForm.newPassword) {
      errors.newPassword = 'New password is required';
      isValid = false;
    } else if (passwordForm.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
      isValid = false;
    }
    
    if (!passwordForm.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
      isValid = false;
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }
    
    setPasswordErrors(errors);
    return isValid;
  };
  
  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateProfileForm()) {
      return;
    }
    
    try {
      await apiService.updateProfile(
        profileForm.firstName,
        profileForm.lastName,
        profileForm.email
      );
      
      setSnackbar({
        open: true,
        message: 'Profile updated successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update profile',
        severity: 'error',
      });
    }
  };
  
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) {
      return;
    }
    
    try {
      await apiService.changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword
      );
      
      // Show success alert in the password section
      setPasswordAlert({
        show: true,
        message: 'Password updated successfully',
        severity: 'success',
      });
      
      // Reset password form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      // Auto-hide the alert after 5 seconds
      setTimeout(() => {
        setPasswordAlert({
          show: false,
          message: '',
          severity: 'success',
        });
      }, 5000);
      
    } catch (error: any) {
      console.error('Error changing password:', error);
      
      // Check if it's a 400 error (current password incorrect)
      const errorMessage = error?.response?.data?.message || 'Failed to update password';
      
      // Show error alert in the password section
      setPasswordAlert({
        show: true,
        message: errorMessage,
        severity: 'error',
      });

      // Auto-hide the alert after 5 seconds
      setTimeout(() => {
        setPasswordAlert({
          show: false,
          message: '',
          severity: 'error',
        });
      }, 5000);
    }
  };
  
  const handlePreferencesSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // In a real app, this would call an API to update preferences
    setSnackbar({
      open: true,
      message: 'Preferences updated successfully',
      severity: 'success',
    });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };
  
  return (
    <Box>
      <PageHeader
        title="Settings"
        subtitle="Manage your account settings and preferences"
      />
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Profile Settings */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Profile Information
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box component="form" onSubmit={handleProfileSubmit}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mr: 2,
                    bgcolor: theme.palette.primary.main,
                    fontSize: '2rem',
                  }}
                >
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {user?.firstName} {user?.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.email}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1 }}
                    disabled
                  >
                    Change Avatar
                  </Button>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={profileForm.firstName}
                    onChange={handleProfileChange}
                    error={!!profileErrors.firstName}
                    helperText={profileErrors.firstName}
                  />
                </Box>
                <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={profileForm.lastName}
                    onChange={handleProfileChange}
                    error={!!profileErrors.lastName}
                    helperText={profileErrors.lastName}
                  />
                </Box>
              </Box>
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={profileForm.email}
                  onChange={handleProfileChange}
                  error={!!profileErrors.email}
                  helperText={profileErrors.email}
                />
              </Box>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Save Changes
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        {/* Password Settings */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Change Password
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            {/* Password Alert */}
            {passwordAlert.show && (
              <Alert 
                severity={passwordAlert.severity} 
                sx={{ mb: 3 }}
                onClose={() => setPasswordAlert({ show: false, message: '', severity: 'success' })}
              >
                {passwordAlert.message}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handlePasswordSubmit}>
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Current Password"
                  name="currentPassword"
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordChange}
                  error={!!passwordErrors.currentPassword}
                  helperText={passwordErrors.currentPassword}
                />
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                  <TextField
                    fullWidth
                    label="New Password"
                    name="newPassword"
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    error={!!passwordErrors.newPassword}
                    helperText={passwordErrors.newPassword}
                  />
                </Box>
                <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    name="confirmPassword"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    error={!!passwordErrors.confirmPassword}
                    helperText={passwordErrors.confirmPassword}
                  />
                </Box>
              </Box>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Update Password
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        {/* Preferences */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Preferences
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box component="form" onSubmit={handlePreferencesSubmit}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.darkMode}
                    onChange={handlePreferenceChange}
                    name="darkMode"
                    color="primary"
                  />
                }
                label="Dark Mode"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.emailNotifications}
                    onChange={handlePreferenceChange}
                    name="emailNotifications"
                    color="primary"
                  />
                }
                label="Email Notifications"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.desktopNotifications}
                    onChange={handlePreferenceChange}
                    name="desktopNotifications"
                    color="primary"
                  />
                }
                label="Desktop Notifications"
              />
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Save Preferences
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
